import CheckCircleIcon from "@assets/images/check-circle.svg";
import EOrderingLogo from "@assets/images/eOrdering.png";
import SCGP_Color from "@assets/images/SCGP-color.svg";
import { Box } from "@material-ui/core";
import { isStaffRole } from "@saleor/auth/hooks/useUserRole";
import Container from "@saleor/components/Container";
import HeadColored from "@saleor/components/HeadColored";
import MyTypography from "@saleor/components/MyTypography";
import PageHeader from "@saleor/components/PageHeader";
import CashSaleStatus from "@saleor/createOrChangeNotRefContract/views/PreviewOrder/CashSaleStatus";
import {
  previewOrderListUrl,
  PreviewOrderQueryParam
} from "@saleor/createOrder/urls";
import {
  useDownloadPdfOrderMutation,
  usePrintChangeOrderMutation,
  useUpdateRequestDateOnIplanMutation
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useListSettings from "@saleor/hooks/useListSettings";
import useLocalPaginator, {
  useLocalPaginationState
} from "@saleor/hooks/useLocalPaginator";
import useNavigator from "@saleor/hooks/useNavigator";
import { ListViews } from "@saleor/types";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { getSortParams, getSortUrlVariables } from "@saleor/utils/sort";
import { parse as parseQs } from "qs";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";

import ConfirmModal from "./ConfirmModal";
import PreviewOrderFooter from "./Footer";
import PreviewOrderHeader from "./Header";
import NotMatchModal from "./NotMatchModal";
import useStyle from "./styles";
import PreviewOrderTable from "./Table";
import { useDataPreview } from "./useDataPreview";

export interface PreviewOrderProps {
  params: PreviewOrderQueryParam;
  id: string;
}
const PreviewOrder: FC<PreviewOrderProps> = ({ id, params }) => {
  const classes = useStyle();
  const navigate = useNavigator();
  const qs = parseQs(location.search.substr(1));

  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const [, dispatchAppState] = useAppState();

  const isStaff = isStaffRole();

  useEffect(() => {
    dispatchAppState({
      type: "setFullScreen",
      payload: {
        value: true
      }
    });
    return () => {
      dispatchAppState({
        type: "setFullScreen",
        payload: {
          value: false
        }
      });
    };
  }, []);

  useEffect(() => {
    if (!isStaff) {
      const handlePopState = () => {
        window.location.href = "/dashboard/sold-to";
      };
      window.history.pushState(null, null, window.location.pathname);
      window.addEventListener("popstate", handlePopState);
      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    }
  }, []);

  const { updateListSettings, settings } = useListSettings(
    ListViews.FILTER_ORDER
  );

  const [
    valuesPaginationState,
    setValuesPaginationState
  ] = useLocalPaginationState(settings?.rowNumber);

  const paginateValues = useLocalPaginator(setValuesPaginationState);

  const {
    dataPreview,
    loadingPreviewOrder,
    refetchPreviewCustomer
  } = useDataPreview(id, params, valuesPaginationState, settings);

  const [updateRequestDate] = useUpdateRequestDateOnIplanMutation();

  const [downloadPdfOrder] = useDownloadPdfOrderMutation();
  const [printChangeOrder] = usePrintChangeOrderMutation();

  const [_, setOpenNotMatchModal] = useState<boolean>(false);

  const { loadNextPage, loadPreviousPage, pageInfo } = paginateValues(
    dataPreview?.previewOrderLines?.pageInfo,
    valuesPaginationState
  );

  useEffect(() => {
    updateListSettings("rowNumber", 50);
  }, []);

  const isFirstTimeShowMatchModal = useRef(true);

  useEffect(() => {
    if (
      dataPreview?.__typename === "PreviewOrderLines" &&
      isFirstTimeShowMatchModal.current &&
      dataPreview?.failOrderLines
    ) {
      setOpenNotMatchModal(!!dataPreview?.failOrderLines.length);
      isFirstTimeShowMatchModal.current = false;
    }
  }, [dataPreview]);

  const resetPaginationState = () => {
    setValuesPaginationState({
      first: settings?.rowNumber,
      after: undefined,
      before: undefined
    });
  };

  const lastPaginationState = () => {
    setValuesPaginationState({
      last: dataPreview?.previewOrderLines?.latestPageItemNumber
    });
  };

  const handleNextLastPage = () => {
    lastPaginationState();
  };

  const handlePreviousFirstPage = () => {
    resetPaginationState();
  };

  const toggleModalNotMatch = () => {
    setOpenNotMatchModal(prev => !prev);
  };

  const toggleModalConfirm = () => {
    setOpenConfirmModal(prev => !prev);
  };

  const handleConfirmNotMatch = async () => {
    if (dataPreview?.__typename === "PreviewOrderLines") {
      const orderLinesId = dataPreview?.failOrderLines.map(item => item.id);
      await updateRequestDate({
        variables: { orderLinesId }
      });
      refetchPreviewCustomer();
      toggleModalNotMatch();
      toggleModalConfirm();
    }
  };

  const handleCancelNotMatch = async () => {
    if (dataPreview?.__typename === "PreviewOrderLines") {
      const orderLinesId = dataPreview?.failOrderLines.map(item => item.id);
      await updateRequestDate({
        variables: { orderLinesId, confirm: false }
      });
      refetchPreviewCustomer();
      toggleModalNotMatch();
      toggleModalConfirm();
    }
  };

  const previewOrderLines = useMemo(() => {
    if (dataPreview?.__typename === "PreviewOrderLines") {
      return mapEdgesToItems(dataPreview?.previewOrderLines);
    }
    if (dataPreview?.__typename === "PreviewDomesticOrderLines") {
      return mapEdgesToItems(dataPreview?.previewOrderLines);
    }
  }, [dataPreview]);

  const handleSort = field => {
    resetPaginationState();
    navigate(
      previewOrderListUrl(id, {
        ...params,
        ...getSortUrlVariables(field, params)
      }),
      { replace: true }
    );
  };

  return (
    <>
      <HeadColored fixedTop />
      <Container className={classes.container}>
        <Box className={classes.pageHeader}>
          <PageHeader title="Preview Order" />
          <div className={classes.headerPageLogo}>
            <img
              src={SCGP_Color}
              alt="SCGP-logo"
              className={classes.scgpLogo}
            />
            <img
              src={EOrderingLogo}
              alt="EOrdering-logo"
              className={classes.eOrderingLogo}
            />
          </div>
        </Box>
        <Box className={classes.headerTitle}>
          <div>
            <img
              className={classes.checkCircleIcon}
              src={CheckCircleIcon}
              alt="success"
            />
            <MyTypography variant="body1" className={classes.headerContent}>
              {qs?.rootPage
                ? "เปลี่ยนคำสั่งซื้อเสร็จสมบูรณ์"
                : "สร้างคำสั่งซื้อเสร็จสมบูรณ์"}
            </MyTypography>
          </div>
          {dataPreview?.__typename === "PreviewDomesticOrderLines" && (
            <CashSaleStatus status={qs?.cashSaleStatus} />
          )}
        </Box>

        <PreviewOrderHeader previewOrder={dataPreview} />
        {(!loadingPreviewOrder || previewOrderLines) && (
          <PreviewOrderTable
            previewOrderLines={previewOrderLines}
            disabled={loadingPreviewOrder}
            pageInfo={pageInfo}
            onNextPage={loadNextPage}
            onPreviousPage={loadPreviousPage}
            onUpdateListSettings={updateListSettings}
            onRowClick={undefined}
            sort={getSortParams(params)}
            onSort={handleSort}
            settings={settings}
            onPreviousFirstPage={handlePreviousFirstPage}
            onNextLastPage={handleNextLastPage}
            totalCount={dataPreview?.previewOrderLines?.totalCount}
            latestPageItemNumber={
              dataPreview?.previewOrderLines?.latestPageItemNumber
            }
          />
        )}
        <PreviewOrderFooter
          params={params}
          onDownloadPdf={downloadPdfOrder}
          onPrintChangeOrder={printChangeOrder}
          orderId={id}
          previewOrder={dataPreview}
        />
        {dataPreview?.__typename === "PreviewOrderLines" && (
          <NotMatchModal
            open={false}
            onConfirm={handleConfirmNotMatch}
            onCancel={handleCancelNotMatch}
            failOrderLines={dataPreview?.failOrderLines}
          />
        )}

        <ConfirmModal open={openConfirmModal} onClose={toggleModalConfirm} />
      </Container>
    </>
  );
};

export default PreviewOrder;
